import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button, Card, Input, Label } from '@features/ui';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@lib/utils.ts';

const schema = yup.object({
  email: yup.string().email('Email is invalid').required('Email is required'),
});

export type ForgotPasswordFormData = yup.InferType<typeof schema>;

type Props = {
  className?: string;
  description?: string;
  labelButton?: string;
  onSubmit: (data: ForgotPasswordFormData) => void;
};

export const ForgotPasswordForm: React.FC<Props> = ({
  className,
  description,
  labelButton,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  return (
    <form
      autoComplete="off"
      className="w-full lg:max-w-[584px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Card
        className={cn(
          className,
          `py-8 pr-8 pl-8 xl:py-12.5 xl:pl-23.25 xl:pr-21.75`
        )}
      >
        <h3 className="text-center text-[27px] mb-5">
          {t('common.form.forgotPassword')}
        </h3>
        <p className="text-center mb-7">
          {description ?? t('forgotPassword.form.description')}
        </p>
        <div>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5 mb-5">
                <Label htmlFor={field.name}>{t('common.form.email')}</Label>
                <div>
                  <Input
                    {...field}
                    error={!!errors.email}
                    id={field.name}
                    inputSize={'lg'}
                    placeholder={t('common.form.email')}
                  />
                  {errors.email && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Button
            className="w-full font-bold mb-5 text-base"
            loading={isSubmitting}
            size="lg"
            type="submit"
            variant="gradient"
          >
            {labelButton ?? t('forgotPassword.form.sendLink')}
          </Button>
          <Button
            className="w-full font-bold mb-5 text-base"
            size="lg"
            type="button"
            variant="outline"
            onClick={() => navigate('/signin')}
          >
            {t('forgotPassword.form.backToLogin')}
          </Button>
        </div>
      </Card>
    </form>
  );
};
